"use client";

import { useId, useState, useTransition, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import {
  LinkIcon,
  Plus,
  Settings2,
  UsersRound,
  VolumeX,
} from "lucide-react";
import {
  createSquad,
  createSquadInvite,
  leaveSquad,
  updateSquadMemberSettings,
  updateSquadSocialSettings,
} from "@/app/actions/companion-squads";
import {
  formatSquadMemberCap,
  getSquadFeedEmptyStateCopy,
  SQUAD_ICON_KEYS,
} from "@/lib/companion/squads";
import type { CompanionSquadSummary } from "@/lib/companion/squads-server";

type SquadManagementCardProps = {
  squads: CompanionSquadSummary[];
};

export type SquadSettingsFormSquad = Pick<
  CompanionSquadSummary,
  | "id"
  | "name"
  | "shareRollCall"
  | "shareStatus"
  | "shareLeaderboards"
  | "socialEnabled"
  | "socialPushConsent"
  | "socialEmailConsent"
  | "notificationsMuted"
  | "momentSharingEnabled"
  | "locationSharingEnabled"
>;

type ActionStatus = {
  tone: "success" | "error";
  message: string;
};

type SquadActionResult = Awaited<ReturnType<typeof createSquad>>;

export function SquadManagementCard({ squads }: SquadManagementCardProps) {
  const router = useRouter();
  const nameId = useId();
  const iconId = useId();
  const [name, setName] = useState("");
  const [iconKey, setIconKey] = useState("");
  const [status, setStatus] = useState<ActionStatus | null>(null);
  const [pending, startTransition] = useTransition();
  const feedCopy = getSquadFeedEmptyStateCopy(squads.length > 0);

  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(null);

    startTransition(() => {
      void (async () => {
        const result = await createSquad({
          name,
          iconKey: iconKey || null,
        });

        if (!result.success) {
          setStatus({
            tone: "error",
            message: result.error ?? "Could not create that Squad right now.",
          });
          return;
        }

        setName("");
        setIconKey("");
        setStatus({ tone: "success", message: "Squad created." });
        queueSquadRefresh(router);
      })();
    });
  }

  return (
    <section
      aria-labelledby="squad-management-heading"
      className="rounded-md border border-border-light bg-white p-4 md:p-5"
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
            Your people
          </p>
          <h2 id="squad-management-heading" className="font-serif text-[28px] leading-tight text-navy">
            Squads
          </h2>
          <p className="mt-2 max-w-[54ch] text-[13.5px] leading-relaxed text-text-muted">
            {feedCopy}
          </p>
        </div>

        <form onSubmit={handleCreate} className="rounded-sm border border-border-light bg-warm-bg p-3">
          <label htmlFor={nameId} className="text-[12px] font-semibold text-navy">
            Squad name
          </label>
          <input
            id={nameId}
            name="squad-name"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Dawn Patrol"
            className="mt-2 min-h-[44px] w-full rounded-sm border border-border bg-white px-3 py-2 text-[13px] text-navy outline-none focus:border-gold"
          />

          <label htmlFor={iconId} className="mt-3 block text-[12px] font-semibold text-navy">
            Icon
          </label>
          <select
            id={iconId}
            name="squad-icon"
            value={iconKey}
            onChange={(event) => setIconKey(event.target.value)}
            className="mt-2 min-h-[44px] w-full rounded-sm border border-border bg-white px-3 py-2 text-[13px] font-semibold text-navy outline-none focus:border-gold"
          >
            <option value="">Default icon</option>
            {SQUAD_ICON_KEYS.map((key) => (
              <option key={key} value={key}>
                {squadIconLabel(key)}
              </option>
            ))}
          </select>

          <button
            type="submit"
            disabled={pending}
            className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-sm bg-navy px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-navy/90 disabled:cursor-wait disabled:bg-navy/55"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {pending ? "Creating" : "Create Squad"}
          </button>
          {status ? <ActionStatusMessage status={status} /> : null}
        </form>
      </div>

      {squads.length > 0 ? (
        <ul className="mt-5 divide-y divide-border-light border-y border-border-light">
          {squads.map((squad) => (
            <li
              key={`${squad.id}-${squad.updatedAt}-${squad.notificationsMuted}-${squad.socialEnabled}`}
              className="py-4"
            >
              <SquadRow squad={squad} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-5 rounded-sm border border-dashed border-border bg-warm-bg px-4 py-5">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-white text-navy">
              <UsersRound className="h-5 w-5" aria-hidden="true" />
            </span>
            <p className="text-[13px] leading-relaxed text-gray-700">
              No Squads yet. Create one above, or find people already out there
              through the Crew directory on this page.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

function SquadRow({ squad }: { squad: CompanionSquadSummary }) {
  const router = useRouter();
  const formId = useId();
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<ActionStatus | null>(null);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [pending, startTransition] = useTransition();

  function runSquadAction(
    action: () => Promise<SquadActionResult>,
    successMessage: string,
    onSuccess?: (result: SquadActionResult) => void,
  ) {
    setStatus(null);
    startTransition(() => {
      void (async () => {
        const result = await action();
        if (!result.success) {
          setStatus({
            tone: "error",
            message: result.error ?? "Could not update that Squad right now.",
          });
          return;
        }

        onSuccess?.(result);
        setStatus({ tone: "success", message: successMessage });
        queueSquadRefresh(router);
      })();
    });
  }

  function handleCreateInvite() {
    runSquadAction(
      () => createSquadInvite({ squadId: squad.id }),
      "Invite link created.",
      (result) => setInviteUrl(result.inviteUrl ?? null),
    );
  }

  function handleLeave() {
    runSquadAction(() => leaveSquad(squad.id), "Left Squad.", () => {
      setConfirmingLeave(false);
    });
  }

  async function copyInviteUrl() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setStatus({ tone: "success", message: "Invite link copied." });
    } catch {
      setStatus({ tone: "error", message: "Copy the invite link manually." });
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] lg:items-start">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-warm-bg text-navy">
            <UsersRound className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold leading-tight text-navy">{squad.name}</h3>
            <p className="mt-0.5 text-[12px] text-text-muted">
              {formatSquadMemberCap(squad.memberCount)} | {squad.role === "owner" ? "Owner" : "Member"}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <span className="inline-flex min-h-[32px] items-center gap-2 rounded-full border border-border bg-warm-bg px-3 py-1 text-[12px] font-semibold text-navy">
            <VolumeX className="h-3.5 w-3.5" aria-hidden="true" />
            {squad.notificationsMuted ? "Muted" : "Notifications on"}
          </span>
          <span className="inline-flex min-h-[32px] items-center rounded-full border border-border bg-warm-bg px-3 py-1 text-[12px] font-semibold text-navy">
            {squad.momentSharingEnabled ? "Moments shared" : "Moments hidden"}
          </span>
          <span className="inline-flex min-h-[32px] items-center rounded-full border border-border bg-warm-bg px-3 py-1 text-[12px] font-semibold text-navy">
            {squad.locationSharingEnabled ? "Location shared" : "Location hidden"}
          </span>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleCreateInvite}
            disabled={pending}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white transition-colors hover:bg-navy/90 disabled:cursor-wait disabled:bg-navy/55"
          >
            <LinkIcon className="h-4 w-4" aria-hidden="true" />
            Create invite
          </button>
          <button
            type="button"
            onClick={() => setConfirmingLeave((value) => !value)}
            disabled={pending}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy transition-colors hover:border-gold/50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Leave
          </button>
        </div>

        {inviteUrl ? (
          <div className="mt-3 rounded-sm border border-border-light bg-warm-bg px-3 py-3">
            <label htmlFor={`${formId}-invite`} className="text-[12px] font-semibold text-navy">
              Invite link
            </label>
            <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
              <input
                id={`${formId}-invite`}
                readOnly
                value={inviteUrl}
                className="min-h-[44px] min-w-0 rounded-sm border border-border bg-white px-3 py-2 text-[12px] text-text-muted"
              />
              <button
                type="button"
                onClick={copyInviteUrl}
                className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy transition-colors hover:border-gold/50"
              >
                Copy
              </button>
            </div>
          </div>
        ) : null}

        {confirmingLeave ? (
          <div className="mt-3 rounded-sm border border-amber-200 bg-amber-50 px-3 py-3">
            <p className="text-[12px] leading-relaxed text-amber-950">
              Leave {squad.name}? You can rejoin only with a fresh invite.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleLeave}
                disabled={pending}
                className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white transition-colors hover:bg-navy/90 disabled:cursor-wait disabled:bg-navy/55"
              >
                Confirm
              </button>
              <button
                type="button"
                onClick={() => setConfirmingLeave(false)}
                disabled={pending}
                className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy transition-colors hover:border-gold/50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        {status ? <ActionStatusMessage status={status} /> : null}
      </div>

      <div className="space-y-3">
        <SquadSocialSettingsForm squad={squad} />
        <SquadPrivateSharingForm squad={squad} />
      </div>
    </div>
  );
}

export function SquadSocialSettingsForm({
  squad,
  readOnly = false,
}: {
  squad: SquadSettingsFormSquad;
  readOnly?: boolean;
}) {
  const router = useRouter();
  const [settings, setSettings] = useState({
    shareRollCall: squad.shareRollCall,
    shareStatus: squad.shareStatus,
    shareLeaderboards: squad.shareLeaderboards,
    socialEnabled: squad.socialEnabled,
    socialPushConsent: squad.socialPushConsent,
    socialEmailConsent: squad.socialEmailConsent,
    notificationsMuted: squad.notificationsMuted,
  });
  const [status, setStatus] = useState<ActionStatus | null>(null);
  const [pending, startTransition] = useTransition();

  function updateSetting(key: keyof typeof settings, checked: boolean) {
    setSettings((current) => {
      const next = { ...current, [key]: checked };
      if (key === "socialEnabled" && !checked) {
        next.socialPushConsent = false;
        next.socialEmailConsent = false;
      }
      return next;
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (readOnly) return;
    setStatus(null);
    startTransition(() => {
      void (async () => {
        const result = await updateSquadSocialSettings({ squadId: squad.id, ...settings });
        if (!result.success) {
          setStatus({
            tone: "error",
            message: result.error ?? "Could not update that Squad right now.",
          });
          return;
        }
        setStatus({ tone: "success", message: "Squad settings updated." });
        queueSquadRefresh(router);
      })();
    });
  }

  const controls: Array<{
    key: keyof typeof settings;
    label: string;
    disabled?: boolean;
  }> = [
    { key: "shareRollCall", label: "Share roll call" },
    { key: "shareStatus", label: "Share approximate status" },
    { key: "shareLeaderboards", label: "Join Squad leaderboards" },
    { key: "socialEnabled", label: "Social moments" },
    {
      key: "socialPushConsent",
      label: "Social push notifications",
      disabled: !settings.socialEnabled,
    },
    {
      key: "socialEmailConsent",
      label: "Social email notifications",
      disabled: !settings.socialEnabled,
    },
    { key: "notificationsMuted", label: "Mute this Squad" },
  ];

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-sm border border-border-light bg-warm-bg px-3 py-3"
    >
      <div className="flex items-center gap-2">
        <Settings2 className="h-4 w-4 text-navy" aria-hidden="true" />
        <h3 className="text-[12px] font-semibold text-navy">{squad.name} settings</h3>
      </div>
      <p className="mt-2 text-[11.5px] leading-relaxed text-gray-700">
        GPS tracks and exact location are never shared through these controls.
      </p>

      <div className="mt-3 space-y-1">
        {controls.map((control) => (
          <label
            key={control.key}
            className="flex min-h-[44px] items-center gap-3 text-[13px] font-semibold text-navy"
          >
            <input
              type="checkbox"
              checked={settings[control.key]}
              disabled={readOnly || control.disabled}
              onChange={(event) => updateSetting(control.key, event.target.checked)}
              className="h-4 w-4 accent-navy disabled:cursor-not-allowed disabled:opacity-50"
            />
            {control.label}
          </label>
        ))}
      </div>

      <button
        type="submit"
        disabled={readOnly || pending}
        className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy transition-colors hover:border-gold/50 disabled:cursor-wait disabled:opacity-60"
      >
        {pending ? "Saving" : "Save Squad settings"}
      </button>
      {status ? <ActionStatusMessage status={status} /> : null}
    </form>
  );
}

export function SquadPrivateSharingForm({
  squad,
  readOnly = false,
}: {
  squad: SquadSettingsFormSquad;
  readOnly?: boolean;
}) {
  const router = useRouter();
  const [momentSharingEnabled, setMomentSharingEnabled] = useState(squad.momentSharingEnabled);
  const [locationSharingEnabled, setLocationSharingEnabled] = useState(
    squad.locationSharingEnabled,
  );
  const [status, setStatus] = useState<ActionStatus | null>(null);
  const [pending, startTransition] = useTransition();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (readOnly) return;
    setStatus(null);
    startTransition(() => {
      void (async () => {
        const result = await updateSquadMemberSettings({
          squadId: squad.id,
          momentSharingEnabled,
          locationSharingEnabled,
        });
        if (!result.success) {
          setStatus({
            tone: "error",
            message: result.error ?? "Could not update private sharing right now.",
          });
          return;
        }
        setStatus({ tone: "success", message: "Private sharing updated." });
        queueSquadRefresh(router);
      })();
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-sm border border-border-light bg-white px-3 py-3"
    >
      <p className="text-[12px] font-semibold text-navy">Separate private sharing</p>
      <p className="mt-2 text-[11.5px] leading-relaxed text-text-muted">
        Session feed sharing and the exact live map are separate from Social notifications.
      </p>
      <label className="mt-2 flex min-h-[44px] items-center gap-3 text-[13px] font-semibold text-navy">
        <input
          type="checkbox"
          checked={momentSharingEnabled}
          disabled={readOnly}
          onChange={(event) => setMomentSharingEnabled(event.target.checked)}
          className="h-4 w-4 accent-navy"
        />
        Share session moments
      </label>
      <label className="flex min-h-[44px] items-center gap-3 text-[13px] font-semibold text-navy">
        <input
          type="checkbox"
          checked={locationSharingEnabled}
          disabled={readOnly}
          onChange={(event) => setLocationSharingEnabled(event.target.checked)}
          className="h-4 w-4 accent-navy"
        />
        Share exact live location
      </label>
      <button
        type="submit"
        disabled={readOnly || pending}
        className="mt-2 inline-flex min-h-[44px] w-full items-center justify-center rounded-sm border border-border bg-warm-bg px-3 py-2 text-[12px] font-semibold text-navy hover:border-gold/50 disabled:cursor-wait disabled:opacity-60"
      >
        {pending ? "Saving" : "Save private sharing"}
      </button>
      {status ? <ActionStatusMessage status={status} /> : null}
    </form>
  );
}

function ActionStatusMessage({ status }: { status: ActionStatus }) {
  return (
    <p
      aria-live="polite"
      className={`mt-3 text-[12px] leading-relaxed ${
        status.tone === "error" ? "text-rose-700" : "text-emerald-700"
      }`}
    >
      {status.message}
    </p>
  );
}

function squadIconLabel(key: string): string {
  return key
    .split("-")
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function queueSquadRefresh(router: ReturnType<typeof useRouter>) {
  window.setTimeout(() => {
    router.refresh();
  }, 700);
}
